// Low-level GEDCOM: bytes → text (with encoding detection), text ↔ RawRecord trees.
// Knows nothing about people or families.

import type { RawRecord } from '../model/types';

// ---------------------------------------------------------------- decoding

// ANSEL (ANSI Z39.47) as used by older GEDCOM 5.x files. Combining marks come
// *before* the base letter in ANSEL but *after* it in Unicode, so we reorder.
const ANSEL_SPECIAL: Record<number, string> = {
  0xa1: 'Ł', 0xa2: 'Ø', 0xa3: 'Đ', 0xa4: 'Þ', 0xa5: 'Æ', 0xa6: 'Œ', 0xa7: 'ʹ', 0xa8: '·', 0xa9: '♭',
  0xaa: '®', 0xab: '±', 0xac: 'Ơ', 0xad: 'Ư', 0xae: 'ʼ', 0xb0: 'ʻ', 0xb1: 'ł', 0xb2: 'ø', 0xb3: 'đ',
  0xb4: 'þ', 0xb5: 'æ', 0xb6: 'œ', 0xb7: 'ʺ', 0xb8: 'ı', 0xb9: '£', 0xba: 'ð', 0xbc: 'ơ', 0xbd: 'ư',
  0xbe: '□', 0xbf: '■', 0xc0: '°', 0xc1: 'ℓ', 0xc2: '℗', 0xc3: '©', 0xc4: '♯', 0xc5: '¿', 0xc6: '¡',
  0xc7: 'ß', 0xc8: '€', 0xcd: 'e', 0xce: 'o', 0xcf: 'ß',
};
const ANSEL_COMBINING: Record<number, string> = {
  0xe0: '̉', 0xe1: '̀', 0xe2: '́', 0xe3: '̂', 0xe4: '̃', 0xe5: '̄',
  0xe6: '̆', 0xe7: '̇', 0xe8: '̈', 0xe9: '̌', 0xea: '̊', 0xeb: '︠',
  0xec: '︡', 0xed: '̕', 0xee: '̋', 0xef: '̐', 0xf0: '̧', 0xf1: '̨',
  0xf2: '̣', 0xf3: '̤', 0xf4: '̥', 0xf5: '̳', 0xf6: '̲', 0xf7: '̦',
  0xf8: '̜', 0xf9: '̮', 0xfa: '︢', 0xfb: '︣', 0xfe: '̓',
};

export function decodeAnsel(bytes: Uint8Array): string {
  let out = '';
  let pending = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (ANSEL_COMBINING[b]) {
      pending += ANSEL_COMBINING[b];
      continue;
    }
    const ch = b < 0x80 ? String.fromCharCode(b) : ANSEL_SPECIAL[b] ?? '�';
    out += ch + pending;
    pending = '';
  }
  return (out + pending).normalize('NFC');
}

export function decodeGedcomBytes(bytes: Uint8Array): { text: string; encoding: string } {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
    return { text: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'UTF-8' };
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return { text: new TextDecoder('utf-16le').decode(bytes.subarray(2)), encoding: 'UTF-16LE' };
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return { text: new TextDecoder('utf-16be').decode(bytes.subarray(2)), encoding: 'UTF-16BE' };
  // BOM-less UTF-16: "0\0" or "\00" at the start.
  if (bytes[0] === 0x30 && bytes[1] === 0x00) return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'UTF-16LE' };
  if (bytes[0] === 0x00 && bytes[1] === 0x30) return { text: new TextDecoder('utf-16be').decode(bytes), encoding: 'UTF-16BE' };

  const head = new TextDecoder('latin1').decode(bytes.subarray(0, 8192));
  const charMatch = head.match(/^\s*1\s+CHAR\s+(\S+)/m);
  const declared = (charMatch?.[1] ?? '').toUpperCase();
  if (declared === 'ANSEL') return { text: decodeAnsel(bytes), encoding: 'ANSEL' };
  if (declared === 'ANSI' || declared.startsWith('WINDOWS') || declared === 'CP1252' || declared === 'IBMPC')
    return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'Windows-1252' };
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(bytes), encoding: 'UTF-8' };
  } catch {
    // Files that claim UTF-8/ASCII but contain stray Latin-1 bytes are common.
    return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'Windows-1252 (fallback)' };
  }
}

// ---------------------------------------------------------------- parsing

const LINE_RE = /^\s*(\d+)\s+(?:(@[^@\s]+@)\s+)?([A-Za-z0-9_]+)(?: (.*))?$/;
const POINTER_RE = /^@[^@\s]+@$/;

export interface ParseResult {
  records: RawRecord[];
  warnings: string[];
}

export function parseGedcomText(text: string): ParseResult {
  const warnings: string[] = [];
  const records: RawRecord[] = [];
  const stack: RawRecord[] = [];
  const lines = text.replace(/^﻿/, '').split(/\r\n|\r|\n/);
  let bad = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const m = line.match(LINE_RE);
    if (!m) {
      // Some exporters wrap long notes without CONT; treat the stray line as a continuation.
      const last = stack[stack.length - 1];
      if (last) last.value = (last.value ?? '') + '\n' + line;
      if (++bad <= 5) warnings.push(`Line ${i + 1} isn't valid GEDCOM and was treated as text: “${line.slice(0, 60)}”`);
      continue;
    }
    const level = Number(m[1]);
    const tag = m[3].toUpperCase();
    let value = m[4];
    if (value !== undefined && !POINTER_RE.test(value)) value = value.replace(/@@/g, '@');
    if (tag === 'CONC' || tag === 'CONT') {
      const target = stack[level - 1];
      if (target) target.value = (target.value ?? '') + (tag === 'CONT' ? '\n' : '') + (value ?? '');
      continue;
    }
    const rec: RawRecord = { tag, children: [] };
    if (m[2]) rec.xref = m[2];
    if (value !== undefined && value !== '') rec.value = value;
    if (level === 0) {
      records.push(rec);
      stack.length = 0;
      stack[0] = rec;
    } else {
      const parent = stack[level - 1];
      if (!parent) {
        if (++bad <= 5) warnings.push(`Line ${i + 1} has level ${level} with no parent and was skipped.`);
        continue;
      }
      parent.children.push(rec);
      stack.length = level;
      stack[level] = rec;
    }
  }
  if (bad > 5) warnings.push(`…and ${bad - 5} more malformed lines.`);
  return { records, warnings };
}

// ---------------------------------------------------------------- writing

export interface WriteOptions {
  version: '5.5.1' | '7.0';
}

const MAX_551_LINE = 240;

/**
 * Pointers ("@I1@") and calendar escapes ("@#DJULIAN@") are written as-is. Other "@"s are
 * doubled: every one in 5.5.1, only a leading one in 7.0.
 */
function escapeValue(v: string, version: WriteOptions['version']): string {
  if (POINTER_RE.test(v)) return v;
  if (version === '7.0') return v.startsWith('@') && !v.startsWith('@#') ? '@' + v : v;
  return v.replace(/@#[^@]*@|@/g, (m) => (m.length > 1 ? m : '@@'));
}

/** Split a long 5.5.1 value into CONC chunks without splitting at a space (readers trim them). */
function concChunks(s: string): string[] {
  const out: string[] = [];
  let rest = s;
  while (rest.length > MAX_551_LINE) {
    let cut = MAX_551_LINE;
    while (cut > 1 && (rest[cut] === ' ' || rest[cut - 1] === ' ')) cut--;
    out.push(rest.slice(0, cut));
    rest = rest.slice(cut);
  }
  out.push(rest);
  return out;
}

export function writeGedcom(records: RawRecord[], opts: WriteOptions): string {
  const lines: string[] = [];
  const emit = (r: RawRecord, level: number) => {
    const head = `${level}${r.xref ? ' ' + r.xref : ''} ${r.tag}`;
    if (r.value === undefined || r.value === '') lines.push(head);
    else {
      const parts = escapeValue(r.value, opts.version).split(/\r\n|\r|\n/);
      parts.forEach((part, i) => {
        const chunks = opts.version === '5.5.1' ? concChunks(part) : [part];
        chunks.forEach((chunk, j) => {
          if (i === 0 && j === 0) lines.push(chunk ? `${head} ${chunk}` : head);
          else if (j === 0) lines.push(chunk ? `${level + 1} CONT ${chunk}` : `${level + 1} CONT`);
          else lines.push(`${level + 1} CONC ${chunk}`);
        });
      });
    }
    for (const c of r.children) emit(c, level + 1);
  };
  for (const r of records) emit(r, 0);
  return lines.join('\r\n') + '\r\n';
}

// ---------------------------------------------------------------- helpers

export function child(r: RawRecord, tag: string): RawRecord | undefined {
  return r.children.find((c) => c.tag === tag);
}

export function childValue(r: RawRecord, tag: string): string | undefined {
  return child(r, tag)?.value;
}

export function childrenOf(r: RawRecord, tag: string): RawRecord[] {
  return r.children.filter((c) => c.tag === tag);
}

export function rec(tag: string, value?: string, children: RawRecord[] = [], xref?: string): RawRecord {
  const r: RawRecord = { tag, children };
  if (value !== undefined && value !== '') r.value = value;
  if (xref) r.xref = xref;
  return r;
}
